/**
 * Admin category-field bindings API layer — PIM Wave P1 Task 8. Typed calls
 * to the SHIPPED PIM Wave P0 `/admin/catalog/categories/{id}/bindings*`
 * routes (`apps/api/garden/features/catalog_schema/router.py:530-628`),
 * gated server-side on `marketplace.attribute.manage`. This task adds NO
 * backend routes.
 *
 * Mirrors the Pydantic schemas in `catalog_schema/schemas.py:495-585`:
 * `Binding{Create,Update,Response,ListResponse,ReorderRequest}`.
 * `matching_weight_override` is a `Decimal|null` on the server, serialised
 * as a JSON string-or-null on the wire (same convention as
 * `attribute-fields/api.ts`'s `matching_weight`).
 *
 * `GET .../bindings` returns a category's DIRECT bindings only — NOT
 * ancestor-resolved (router docstring, `router.py:543-544`). There is no
 * resolved-with-provenance endpoint (USER RULING C3, `p1-task0-recon.md`
 * C3) — `hooks.ts`'s `useCategoryBindings` is called once per category id
 * (the current category AND, via `useQueries`, each ancestor) to assemble
 * the inherited-bindings panel client-side.
 *
 * `PUT .../bindings/reorder` is EXHAUSTIVE: `BindingReorderRequest` requires
 * `ordered_binding_ids` to be exactly the category's current binding-id set
 * (`schemas.py:574-585`) — a partial list 422s
 * (`catalog_schema.binding.reorder_invalid`). This is a batch route (unlike
 * `attribute-fields` options, which have no batch reorder) — dnd persistence
 * always sends the FULL reordered id list, never a per-item PATCH.
 */
import { apiClient, apiFetch } from "@/lib/api-client";

/** `BINDING_REQUIREMENT_VALUES` (`catalog_schema/models.py`). */
export const BINDING_REQUIREMENT_VALUES = [
  "required",
  "recommended",
  "optional",
] as const;
export type BindingRequirementValue = (typeof BINDING_REQUIREMENT_VALUES)[number];

/**
 * Mirrors the `visibility_rule` JSONB shape lock
 * (`catalog_schema/schemas.py:67-102`): exactly `{field_slug, operator,
 * value}`; `operator` is `"eq"|"in"`; `value` must be a list when
 * `operator==="in"`. Task 9 (`VisibilityRuleBuilder`) is the only writer of
 * non-null values here — Task 8 only reads/copies this field verbatim
 * (create-from-override) and never constructs one from scratch.
 */
export interface VisibilityRule {
  field_slug: string;
  operator: "eq" | "in";
  value: unknown;
}

/** Mirrors `BindingResponse` (`schemas.py:554-565`). */
export interface CategoryBinding {
  id: string;
  category_id: string;
  field_id: string;
  requirement: string;
  sort_order: number;
  visibility_rule: VisibilityRule | null;
  /** `Decimal | null` on the server, serialised as a string-or-null. */
  matching_weight_override: string | null;
}

/** Mirrors `BindingListResponse` (`schemas.py:568-571`) — full list, un-paginated. */
export interface CategoryBindingListResponse {
  bindings: CategoryBinding[];
}

/**
 * Body for `POST /admin/catalog/categories/{category_id}/bindings` (mirrors
 * `BindingCreate`, `schemas.py:495-521`). `category_id` comes from the path.
 */
export interface CategoryBindingCreate {
  field_id: string;
  requirement: string;
  sort_order?: number;
  visibility_rule?: VisibilityRule | null;
  matching_weight_override?: string | null;
}

/**
 * Body for `PATCH .../bindings/{binding_id}` (mirrors `BindingUpdate`,
 * `schemas.py:524-551`, partial only). No `field_id` — changing it is
 * "delete + create", not an update.
 */
export interface CategoryBindingUpdate {
  requirement?: string;
  sort_order?: number;
  visibility_rule?: VisibilityRule | null;
  matching_weight_override?: string | null;
}

/**
 * Body for `PUT .../bindings/reorder` (mirrors `BindingReorderRequest`,
 * `schemas.py:574-585`) — EXHAUSTIVE ordered id list (min length 1).
 */
export interface CategoryBindingReorderBody {
  ordered_binding_ids: string[];
}

export function listCategoryBindings(
  categoryId: string,
): Promise<CategoryBindingListResponse> {
  return apiClient.get<CategoryBindingListResponse>(
    `/admin/catalog/categories/${categoryId}/bindings`,
  );
}

export function createCategoryBinding(
  categoryId: string,
  body: CategoryBindingCreate,
): Promise<CategoryBinding> {
  return apiClient.post<CategoryBinding, CategoryBindingCreate>(
    `/admin/catalog/categories/${categoryId}/bindings`,
    body,
  );
}

export function updateCategoryBinding(
  categoryId: string,
  bindingId: string,
  body: CategoryBindingUpdate,
): Promise<CategoryBinding> {
  // PATCH partial-update — no `apiClient.patch` shortcut (recon A0).
  return apiFetch<CategoryBinding, CategoryBindingUpdate>({
    method: "PATCH",
    path: `/admin/catalog/categories/${categoryId}/bindings/${bindingId}`,
    body,
  });
}

export function deleteCategoryBinding(
  categoryId: string,
  bindingId: string,
): Promise<void> {
  // 204 hard delete — no response body.
  return apiClient.delete<void>(
    `/admin/catalog/categories/${categoryId}/bindings/${bindingId}`,
  );
}

export function reorderCategoryBindings(
  categoryId: string,
  body: CategoryBindingReorderBody,
): Promise<CategoryBindingListResponse> {
  return apiClient.put<CategoryBindingListResponse, CategoryBindingReorderBody>(
    `/admin/catalog/categories/${categoryId}/bindings/reorder`,
    body,
  );
}

// ---------------------------------------------------------------------
// Query-key factory (consumed by hooks.ts). ONE shape reused for both the
// current category and every ancestor (USER RULING C3 client-side assembly)
// — `hooks.ts` builds the ancestor fan-out on the SAME key/fetcher pair via
// `useQueries`, not a second key family.
// ---------------------------------------------------------------------

export const categoryBindingKeys = {
  /** `["admin","category-bindings",categoryId]`. */
  list: (categoryId: string) => ["admin", "category-bindings", categoryId] as const,
} as const;

// ---------------------------------------------------------------------
// Public form-schema (PIM Wave P1 Task 10 — FormPreview). Mirrors the
// NESTED `TrilingualText`-shaped DTOs in `catalog_schema/schemas.py:117-193`
// (`FormSchemaResponse`/`FormSchemaSection`/`FormGroupDto`/`FormFieldDto`/
// `FormFieldOptionDto`) — DELIBERATELY DIFFERENT from the FLAT admin DTOs
// above (`name_ru`/`name_ky`/`name_en` triples): this is the SAME contract
// the seller form (P2) will consume, fetched from the PUBLIC, unauthenticated
// `GET /marketplace/categories/{category_id}/form-schema` (`router.py:700`,
// `public_catalog_router` — NOT the `/admin/catalog/*` prefix above). The
// FormPreview component renders ONLY from this response — never from the
// admin bindings/fields data already loaded elsewhere on the page — so the
// preview is zero-drift-by-construction with what sellers will actually see.
//
// Errors (router docstring): 404 `catalog_schema.category.not_found`
// (missing/soft-deleted category); 409 `catalog_schema.category.archived`
// (category itself archived — "pick another category" copy, per the
// service's locked `FormSchemaCategoryArchivedError` decision that archived
// categories are simply unavailable for NEW forms).
// ---------------------------------------------------------------------

/** One RU/KY/EN triple (`TrilingualText`, `schemas.py:104-113`). `ru` is
 * always present; `ky`/`en` pass through verbatim including `null` — the
 * client owns the RU-fallback rendering. */
export interface TrilingualText {
  ru: string;
  ky: string | null;
  en: string | null;
}

/** One surviving (non-archived), already-ordered dropdown option
 * (`FormFieldOptionDto`, `schemas.py:116-126`). */
export interface FormFieldOptionDto {
  value_slug: string;
  label: TrilingualText;
  sort_order: number;
}

/** One resolved, policy-filtered field on the form contract
 * (`FormFieldDto`, `schemas.py:129-152`). `unit_kind`/`canonical_unit`/
 * `allowed_units` populated only for `field_type === "size"`; `options`
 * only for `field_type === "select"`. `hint`/`example` are the WHOLE
 * OBJECT `null` when RU was never authored — not a `TrilingualText` with
 * `ru: null` (P0 law). No `matching_weight` — that is chatbot config, not
 * part of the form contract. */
export interface FormFieldDto {
  field_slug: string;
  field_type: string;
  requirement: string;
  label: TrilingualText;
  hint: TrilingualText | null;
  example: TrilingualText | null;
  sort_order: number;
  visibility_rule: VisibilityRule | null;
  unit_kind: string | null;
  canonical_unit: string | null;
  allowed_units: string[] | null;
  options: FormFieldOptionDto[] | null;
}

/** One `attribute_field_groups` row, trilingual-named (`FormGroupDto`,
 * `schemas.py:155-162`). */
export interface FormGroupDto {
  id: string;
  slug: string;
  name: TrilingualText;
  sort_order: number;
}

/** One rendered section — `group === null` is the ungrouped bucket, which
 * `resolve_form_schema` always places LAST (`FormSchemaSection`,
 * `schemas.py:165-172`). */
export interface FormSchemaSection {
  group: FormGroupDto | null;
  fields: FormFieldDto[];
}

/** The full form contract for one category (`FormSchemaResponse`,
 * `schemas.py:175-193`). `groups` is pre-ordered by the service — render in
 * array order, do not re-sort. */
export interface FormSchemaResponse {
  category_id: string;
  category_slug: string;
  category_name: TrilingualText;
  groups: FormSchemaSection[];
}

export function getFormSchema(categoryId: string): Promise<FormSchemaResponse> {
  return apiClient.get<FormSchemaResponse>(
    `/marketplace/categories/${categoryId}/form-schema`,
  );
}

export const formSchemaKeys = {
  /** `["marketplace","form-schema",categoryId]` (brief §1 — exact shape). */
  detail: (categoryId: string) => ["marketplace", "form-schema", categoryId] as const,
} as const;
